/**
 * Faz download de um ficheiro com um nome previsível.
 *
 * O atributo `<a download>` só é respeitado para URLs same-origin (ou
 * `blob:`/`data:`). O PDF vive no host KOORTEX (r2-proxy), logo é cross-origin:
 * o browser ignora o `download` e usa a última parte do URL (a chave R2 = só o
 * número da proposta). Para forçar o nome, buscamos o ficheiro como blob e
 * criamos um object URL same-origin — aí o `download` já vale.
 *
 * Se o fetch cross-origin falhar (ex.: CORS não permitido no r2-proxy), cai no
 * comportamento antigo: abrir o URL numa nova aba (o nome fica o da chave R2,
 * mas o download não parte).
 */
export async function downloadWithFilename(
  url: string,
  filename: string,
): Promise<void> {
  try {
    const res = await fetch(url, { credentials: "omit" })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    triggerDownload(objectUrl, filename)
    // Revoga depois do click ter tido tempo de iniciar o download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  } catch {
    // Fallback: abrir em nova aba (nome não garantido, mas o PDF abre).
    triggerDownload(url, filename, true)
  }
}

/**
 * Download a partir de bytes base64 (vindos do backend, que os buscou ao host
 * server-side). Cria um Blob same-origin → o `download` com o nome pega sempre,
 * sem depender de CORS no host. Este é o caminho preferido; o
 * `downloadWithFilename` (fetch cross-origin) fica só como fallback.
 */
export function downloadFromBase64(
  base64: string,
  filename: string,
  contentType = "application/pdf",
): void {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: contentType }))
  triggerDownload(objectUrl, filename)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

function triggerDownload(href: string, filename: string, newTab = false): void {
  const a = document.createElement("a")
  a.href = href
  if (newTab) {
    a.target = "_blank"
    a.rel = "noopener"
  }
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
